import { BackendHttpService } from './backendHttpService';
import { 
  SkillMatrixEntry, 
  CreateSkillAssessmentRequest, 
  CreateSkillConfirmationRequest, 
  BulkConfirmSkillAssessmentsRequest,
  PersonWithPendingConfirmations,
  SkillAssessment,
  SkillConfirmationStatus
} from '../models/skill-matrix.model';

export interface CreateSkillAssessmentAsManagerRequest {
  personTechnologyId: number;
  levelManagerRating: number;
  yearsExperience: number | null;
  assessmentComments: string | null;
  managerComments: string | null;
  status: SkillConfirmationStatus;
  personId: number;
  userId: number; // Manager ID
}

export class SkillMatrixService extends BackendHttpService {
  constructor() {
    super('SkillMatrix');
  }

  /**
   * Get skill matrix for a person
   */
  async getSkillMatrixByPerson(personId: number): Promise<SkillMatrixEntry[]> {
    try {
      return await this.get<SkillMatrixEntry[]>(`/person/${personId}`);
    } catch (error) {
      console.error(`Error getting skill matrix for person ${personId}:`, error);
      throw error;
    }
  }

  /**
   * Create skill assessment
   */
  async createSkillAssessment(request: CreateSkillAssessmentRequest): Promise<any> {
    try {
      return await this.post<CreateSkillAssessmentRequest, any>(request, '/assessment');
    } catch (error) {
      console.error('Error creating skill assessment:', error);
      throw error;
    }
  }

  /**
   * Create skill confirmation (manager endpoint)
   */
  async createSkillConfirmation(request: CreateSkillConfirmationRequest): Promise<any> {
    try {
      return await this.post<CreateSkillConfirmationRequest, any>(request, '/confirmation');
    } catch (error) {
      console.error('Error creating skill confirmation:', error);
      throw error;
    }
  }

  /**
   * Bulk confirm all pending skill assessments for a person
   */
  async bulkConfirmSkillAssessments(request: BulkConfirmSkillAssessmentsRequest): Promise<any> {
    try {
      console.log('Bulk confirming skill assessments:', request);
      return await this.post<BulkConfirmSkillAssessmentsRequest, any>(request, '/person-bulk-confirm');
    } catch (error) {
      console.error('Error bulk confirming skill assessments:', error);
      throw error;
    }
  }

  /**
   * Get all people with pending skill confirmations (manager endpoint)
   */
  async getPendingSkillConfirmations(): Promise<PersonWithPendingConfirmations[]> {
    try {
      return await this.get<PersonWithPendingConfirmations[]>('/pending-confirmations');
    } catch (error) {
      console.error('Error getting pending skill confirmations:', error);
      throw error;
    }
  }

  /**
   * Create a skill assessment as a manager (auto-confirmed)
   * This endpoint allows managers to create assessments on behalf of users
   */
  async createAssessmentAsManager(request: CreateSkillAssessmentAsManagerRequest): Promise<SkillAssessment> {
    try {
      const response = await this.post<any, SkillAssessment>(
        {
          personTechnologyId: request.personTechnologyId,
          levelManagerRating: request.levelManagerRating,
          yearsExperience: request.yearsExperience ?? 0,
          assessmentComments: request.assessmentComments ?? '',
          managerComments: request.managerComments ?? '',
          status: request.status,
          personId: request.personId,
          userId: request.userId
        },
        '/assessment/manager'
      );
      
      console.log('Assessment created as manager:', response);
      return response;
    } catch (error) {
      console.error('Error creating assessment as manager:', error);
      
      // Fallback to regular assessment endpoint if manager endpoint fails
      console.log('Falling back to regular assessment endpoint...');
      const fallbackRequest: CreateSkillAssessmentRequest = {
        personId: request.personId,
        personTechnologyId: request.personTechnologyId,
        levelSelfRating: request.levelManagerRating,
        yearsExperience: request.yearsExperience,
        comments: request.assessmentComments
      };
      
      return await this.createSkillAssessment(fallbackRequest);
    }
  }

  /**
   * Update existing skill assessment as manager (manager endpoint)
   * NOTE: This uses the confirmation endpoint to override assessment values
   */
  async updateAssessmentAsManager(assessmentId: number, updateData: { levelSelfRating: number; yearsExperience: number | null; comments: string | null }, personId: number, userId: number): Promise<any> {
    try {
      // Use the confirmation endpoint to "update" by creating a confirmation with new values
      const confirmationRequest: CreateSkillConfirmationRequest = {
        assessmentId: assessmentId,
        levelManagerRating: updateData.levelSelfRating,
        managerComments: updateData.comments,
        status: SkillConfirmationStatus.Approved,
        personId: personId
      };

      return await this.createSkillConfirmation(confirmationRequest);
    } catch (error) {
      console.error('Error updating assessment as manager:', error);
      throw error;
    }
  }
} 